import Head from 'next/head';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CONDITION_DESKTOP, SUPPORT_EMAIL, SUPPORT_TELEGRAM } from 'shared/common/constants';
import styled from 'styled-components';
import { MainLayout } from 'widgets/layouts';

export const About: FC = () => {
  const { t } = useTranslation('header.component');

  return (
    <MainLayout hasFooter head={{ title: 'O проекте Consulty' }} isLoginButtonVisible>
      <Head>
        <meta name="title" content="Правила сервиса Consulty" />
        <meta name="description" content="О проекте Consulty" />
        <meta name="og:title" content="О проекте Consulty" />
        <meta name="og:site_name" content="www.consulty.online" />
        <meta name="og:url" content="https://www.consulty.online/" />
        <meta name="og:description" content="О проекте Consulty" />
        <meta property="og:image" content="/img/social.jpg" />
      </Head>

      <h1 className="hidden">{t('head.title')}</h1>
      <Content className="container">
        <StyledHeading>О проекте</StyledHeading>
        <QuoteContainer>
          <p>
            &laquo;11&nbsp;лет я&nbsp;занимаюсь бизнесом и&nbsp;веду активную жизнь. За&nbsp;это время сотни часов
            я&nbsp;потратил на&nbsp;поиски людей, готовых решать со&nbsp;мной разного рода задачи: проконсультировать,
            подсказать. От&nbsp;того, кто мог&nbsp;бы мне как киноману помочь подобрать хороший звук для домашнего
            кинотеатра, до&nbsp;бухгалтера, хорошо разбирающегося в&nbsp;патентах.
          </p>
          <p>
            В&nbsp;разгар пандемии я&nbsp;больше пяти часов разбирался в&nbsp;том, как выбрать эллипсоид среди сотен
            моделей, вроде&nbsp;бы одинаковых по&nbsp;функциям и&nbsp;параметрам, но&nbsp;стоящих{' '}
            <HighlightText>от&nbsp;20&nbsp;000 до&nbsp;2&nbsp;000&nbsp;000</HighlightText>. Интернет не&nbsp;давал мне
            нужных ответов, но&nbsp;я&nbsp;наткнулся на&nbsp;людей, которые простым языком объяснили, в&nbsp;чем
            принципиальная разница, и&nbsp;что подойдет именно мне.
          </p>
          <p>
            Тогда я&nbsp;понял, что не&nbsp;хочу больше тратить драгоценное время, изучая тонны бесполезной информации в
            интернете, ведь где-то точно есть эксперт в&nbsp;той или иной сфере, который будет рад помочь, сэкономив мои
            время и&nbsp;силы. Причем сделает это легко и&nbsp;с&nbsp;удовольствием. В&nbsp;итоге я&nbsp;решил собрать
            таких людей на&nbsp;одной площадке. Так родилась идея Consulty&raquo;.
          </p>
          <MetaContainer>
            <MetaAvatar>
              <img src="/img/founder.jpg" alt="" />
            </MetaAvatar>
            <MetaDataInner>
              <MetaName>Никита Толстов</MetaName>
              <MetaDesc>основатель Consulty</MetaDesc>
            </MetaDataInner>
          </MetaContainer>
        </QuoteContainer>
        <p>
          Consulty создается командой из&nbsp;нескольких предпринимателей и&nbsp;людей, которые любят делать хорошие
          продукты. Нам важно, чтобы каждый мог найти в&nbsp;нашем сервисе эксперта, который быстро откликнется,
          проведет онлайн консультацию, ответит на&nbsp;ваш вопрос или решит вашу проблему и&nbsp;сделает это
          превосходно.
        </p>
        <p>
          Сейчас мы&nbsp;работаем в&nbsp;тестовом режиме, в&nbsp;первую очередь, фокусируясь на&nbsp;качестве экспертов
          в&nbsp;сервисе. Это наш главный приоритет&nbsp;&mdash; мы&nbsp;стараемся собрать здесь первоклассных
          специалистов. К&nbsp;сожалению, это может занять какое-то время. Помните: если вам не&nbsp;понравилась
          консультация, вы&nbsp;в&nbsp;любую секунду можете запросить возврат денег, и&nbsp;мы&nbsp;вернем их&nbsp;очень
          быстро, без расспросов. Если у&nbsp;вас есть любые идеи или предложения&nbsp;&mdash; пишите нам в{' '}
          <a href={`https://t.me/${SUPPORT_TELEGRAM}`}>Telegram</a> или&nbsp;на{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <p>
          Consulty также потенциально открывает множество идей для партнерств и&nbsp;коллабораций. Если вы&nbsp;хотите
          предложить партнерство, сотрудничество или просто работать с&nbsp;нами&nbsp;&mdash; пишите нам в{' '}
          <a href={`https://t.me/${SUPPORT_TELEGRAM}`}>Telegram</a> или&nbsp;на{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
        <p>Удачных консультаций!</p>
      </Content>
    </MainLayout>
  );
};

const Content = styled.main`
  a {
    color: #8b33ff;
  }
  p {
    max-width: 810px;
    font-size: 19px;
    margin-bottom: 30px;
    line-height: 1.58;
  }
`;

const StyledHeading = styled.h2`
  font-size: 24px;
  color: var(--black2);
  padding: 30px 0 20px;
  ${CONDITION_DESKTOP} {
    padding-top: 60px;
    padding-bottom: 30px;
    text-align: left;
    font-size: 42px;
  }
`;

const QuoteContainer = styled.div`
  padding-left: 30px;
  padding-right: 20px;
  border-left: 2px solid var(--purple);
  margin-bottom: 60px;
`;

const HighlightText = styled.span`
  font-weight: 600;
  color: var(--purple);
`;

const MetaContainer = styled.div`
  display: flex;
`;

const MetaName = styled.div`
  font-size: 19px;
  font-weight: 500;
  color: #232832;
`;

const MetaDataInner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MetaAvatar = styled.div`
  margin-right: 20px;
  width: 90px;
  height: 90px;
  img {
    max-height: 100%;
    max-width: 100%;
    border-radius: 50%;
  }
`;

const MetaDesc = styled.div`
  font-size: 15px;
  font-weight: 500;
  line-height: 1.33;
  color: var(--gray3);
`;
